<template>
  <transition name="modal-fade">
    <div
      v-if="show"
      class="preview-modal-overlay"
      :class="{ 'dragging-active': !isInitialPosition }"
    >
      <div
        ref="modalRef"
        class="preview-modal"
        :class="{ 'is-dragging': isDragging }"
        :style="modalStyle"
        @click.stop
        @mousedown="startDrag"
        @touchstart="startDrag"
      >
        <div class="preview-header" :class="{ draggable: true }">
          <div class="preview-header-content">
            <div class="preview-header-top">
              <span class="preview-category">{{ getCategoryLabel(template?.category) }}</span>
            </div>
            <h4 class="preview-title">{{ template?.name }}</h4>
          </div>
          <div class="preview-header-actions">
            <div class="drag-handle" title="Drag to move">
              <Icon icon="mdi:drag" width="16" height="16" />
            </div>
            <button type="button" class="btn-close-preview" @click="close">
              <Icon icon="mdi:close" width="18" height="18" />
            </button>
          </div>
        </div>
        <div class="preview-body">
          <div class="preview-section">
            <div class="preview-label">
              <Icon icon="mdi:email-outline" width="12" height="12" />
              <span>Subject:</span>
            </div>
            <p class="preview-subject-text">{{ template?.subject }}</p>
          </div>
          <div class="preview-section" v-if="template?.shortcut">
            <div class="preview-label">
              <Icon icon="mdi:keyboard" width="12" height="12" />
              <span>Shortcut:</span>
            </div>
            <div class="preview-shortcut-content">
              <span class="shortcut-text">{{ template.shortcut }}</span>
              <Icon
                icon="mdi:content-copy"
                width="14"
                height="14"
                class="copy-icon"
                @click.stop="copyShortcut"
                title="Copy Shortcut"
              />
            </div>
          </div>
          <div class="preview-section">
            <div class="preview-label">
              <Icon icon="mdi:text-box-outline" width="12" height="12" />
              <span>Content:</span>
            </div>
            <div class="preview-content-text" v-html="template?.content"></div>
          </div>
        </div>
        <div class="preview-footer">
          <div class="preview-stats">
            <button
              class="preview-stat clickable positive"
              @click.stop="markPositive"
              title="Mark Positive"
            >
              <Icon icon="mdi:thumb-up" width="14" height="14" />
              <span>{{ template?.positiveReplies || 0 }}</span>
            </button>
            <button
              class="preview-stat clickable negative"
              @click.stop="markNegative"
              title="Mark Negative"
            >
              <Icon icon="mdi:thumb-down" width="14" height="14" />
              <span>{{ template?.negativeReplies || 0 }}</span>
            </button>
            <button class="btn-copy-template" @click.stop="copyTemplate" title="Copy Template">
              <Icon icon="mdi:content-copy" width="14" height="14" />
              <span>Copy</span>
              <span class="copy-count">({{ template?.copied || 0 }})</span>
            </button>
          </div>
          <div class="preview-actions">
            <button class="btn-edit" @click.stop="editTemplate" title="Edit Template">
              <Icon icon="mdi:pencil" width="14" height="14" />
            </button>
            <button class="btn-delete" @click.stop="deleteTemplate" title="Delete Template">
              <Icon icon="mdi:delete" width="14" height="14" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onBeforeUnmount } from "vue";
import { useToast } from "@/composables/ui/useToast";
import { Icon } from "@iconify/vue";
import { Clipboard } from "v-clipboard";
export default defineComponent({
  name: "TemplatePreviewModal",
  components: { Icon },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    template: {
      type: Object,
      default: null,
    },
    // Left offset (px), measured from the Template Viewer's actual on-screen
    // position, so the detail panel opens right beside it. Falls back to a
    // fixed left-side offset if not provided.
    anchorLeft: {
      type: Number,
      default: null,
    },
  },
  emits: [
    "close",
    "mark-positive",
    "mark-negative",
    "copy-template",
    "edit-template",
    "delete-template",
  ],
  setup(props, { emit }) {
    const $toast = useToast();
    // Dragging state
    const isDragging = ref(false);
    const dragStart = ref({ x: 0, y: 0 });
    const modalPosition = ref({ x: 0, y: 0 });
    const isInitialPosition = ref(true);
    const modalRef = ref<HTMLElement | null>(null);

    // Dynamic positioning for drag functionality
    const modalStyle = computed(() => {
      if (isInitialPosition.value) {
        const fallbackLeft = 40;
        const maxPanelWidth = 500;
        const viewportMargin = 16;
        const rawLeft = props.anchorLeft ?? fallbackLeft;
        // Clamp so the panel can't be pushed off the right edge of the viewport
        // on narrower screens.
        const clampedLeft = Math.max(
          viewportMargin,
          Math.min(rawLeft, window.innerWidth - maxPanelWidth - viewportMargin)
        );

        return {
          position: "fixed" as const,
          left: `${clampedLeft}px`,
          top: "50%",
          right: "auto",
          transform: "translateY(-50%)",
          width: "28%",
          minWidth: "350px",
          maxWidth: "500px",
          minHeight: "60vh",
          maxHeight: "85vh",
          borderRadius: "12px !important",
          zIndex: "10060",
        };
      }

      return {
        position: "fixed" as const,
        left: `${modalPosition.value.x}px`,
        top: `${modalPosition.value.y}px`,
        transform: "none",
        margin: "0",
        width: "28%",
        minWidth: "350px",
        maxWidth: "500px",
        minHeight: "60vh",
        maxHeight: "85vh",
        borderRadius: "12px !important",
        zIndex: "10000",
      };
    });

    const startDrag = (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement;
      const excludedSelectors = [
        ".btn-close-preview",
        ".btn-edit",
        ".btn-delete",
        ".preview-actions",
        ".preview-body",
        ".preview-section",
        ".copy-icon",
        ".preview-stats",
        ".preview-stat",
        ".preview-footer",
      ];

      if (excludedSelectors.some((selector) => target.closest(selector))) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      isDragging.value = true;

      const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
      const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

      if (isInitialPosition.value) {
        const modal = modalRef.value;
        if (modal) {
          const rect = modal.getBoundingClientRect();
          modalPosition.value = { x: rect.left, y: rect.top };
        } else {
          modalPosition.value = {
            x: 40,
            y: window.innerHeight * 0.1,
          };
        }
        isInitialPosition.value = false;
      }

      dragStart.value = {
        x: clientX - modalPosition.value.x,
        y: clientY - modalPosition.value.y,
      };

      const dragEvents = [
        ["mousemove", handleDrag, { passive: false }],
        ["mouseup", stopDrag],
        ["touchmove", handleDrag, { passive: false }],
        ["touchend", stopDrag],
      ] as const;

      dragEvents.forEach(([event, handler, options]) => {
        document.addEventListener(event, handler as any, options);
      });
    };

    const handleDrag = (event: MouseEvent | TouchEvent) => {
      if (!isDragging.value) return;

      event.preventDefault();
      event.stopPropagation();

      const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
      const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

      let newX = clientX - dragStart.value.x;
      let newY = clientY - dragStart.value.y;

      const modal = modalRef.value;
      if (modal) {
        const rect = modal.getBoundingClientRect();
        const padding = 10;
        newX = Math.max(padding, Math.min(newX, window.innerWidth - rect.width - padding));
        newY = Math.max(padding, Math.min(newY, window.innerHeight - rect.height - padding));
      }

      modalPosition.value = { x: newX, y: newY };
    };

    const stopDrag = () => {
      isDragging.value = false;

      const dragEvents = ["mousemove", "mouseup", "touchmove", "touchend"];
      const handlers = [handleDrag, stopDrag, handleDrag, stopDrag];

      dragEvents.forEach((event, index) => {
        document.removeEventListener(event, handlers[index] as any);
      });
    };

    const getCategoryLabel = (category: string) => {
      const categoryMap = {
        All: "All",
        Call: "Call",
        Email: "Email",
        LinkedIn: "LinkedIn",
      };
      return categoryMap[category as keyof typeof categoryMap] || category || "General";
    };

    const close = () => {
      isInitialPosition.value = true;
      modalPosition.value = { x: 0, y: 0 };
      emit("close");
    };

    const markPositive = () => {
      if (props.template) {
        emit("mark-positive", props.template);
      }
    };

    const markNegative = () => {
      if (props.template) {
        emit("mark-negative", props.template);
      }
    };

    const copyShortcut = () => {
      if (props.template?.shortcut) {
        Clipboard.copy(props.template.shortcut);
        $toast?.success("Shortcut copied to clipboard!");
      }
    };

    const htmlToText = (html: string): string => {
      const tmp = document.createElement("DIV");
      tmp.innerHTML = html;

      tmp.querySelectorAll("br").forEach((br) => {
        br.replaceWith(document.createTextNode("\n"));
      });

      tmp.querySelectorAll("p, div, h1, h2, h3, h4, h5, h6").forEach((el) => {
        el.insertAdjacentText("afterend", "\n");
      });

      tmp.querySelectorAll("li").forEach((li) => {
        li.insertAdjacentText("beforeend", "\n");
      });

      let text = tmp.textContent || tmp.innerText || "";
      text = text.replace(/[ \t]+/g, " ");
      text = text.replace(/\r\n/g, "\n");
      text = text.replace(/\n{3,}/g, "\n\n");

      return text.trim();
    };

    const copyTemplate = () => {
      if (props.template) {
        const plainTextContent = htmlToText(props.template.content || "");
        const templateContent = `${props.template.subject}\n\n${plainTextContent}`;
        Clipboard.copy(templateContent);

        emit("copy-template", props.template);
      }
    };

    const editTemplate = () => {
      if (props.template) {
        emit("edit-template", props.template);
      }
    };

    const deleteTemplate = () => {
      if (props.template) {
        emit("delete-template", props.template);
      }
    };

    onBeforeUnmount(() => {
      document.removeEventListener("mousemove", handleDrag);
      document.removeEventListener("mouseup", stopDrag);
      document.removeEventListener("touchmove", handleDrag);
      document.removeEventListener("touchend", stopDrag);
    });

    return {
      isDragging,
      modalStyle,
      modalRef,
      isInitialPosition,
      getCategoryLabel,
      close,
      markPositive,
      markNegative,
      copyShortcut,
      copyTemplate,
      editTemplate,
      deleteTemplate,
      startDrag,
    };
  },
});
</script>

<style scoped>
.preview-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-left: 40px;
  padding-top: 16px;
  padding-bottom: 16px;
  z-index: 10060;
  pointer-events: none;
}

.preview-modal {
  background: #ffffff;
  border-radius: 12px !important;
  width: 28%;
  min-width: 350px;
  max-width: 500px;
  min-height: 80vh;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  transition: box-shadow 0.2s ease;
  user-select: none;
  pointer-events: auto;
  position: relative;
}

.preview-modal.is-dragging {
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
  cursor: grabbing;
  transition: none;
  border-radius: 12px !important;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 14px 16px;
  border-bottom: 1px solid #e8e8e8;
  transition: background-color 0.2s ease;
}

.preview-header.draggable {
  cursor: grab;
  user-select: none;
  transition: background-color 0.2s ease;
}

.preview-header.draggable:hover {
  background-color: #f8f9fa;
}

.preview-header.draggable:active {
  cursor: grabbing;
  background-color: #e9ecef;
}

.preview-header-content {
  flex: 1;
}

.preview-header-top {
  margin-bottom: 4px;
}

.copy-icon {
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 2px;
  border-radius: 3px;
}

.copy-icon:hover {
  color: #3e3efe;
  background-color: #f3f1ff;
  transform: scale(1.1);
}

.preview-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.drag-handle {
  color: #9ca3af;
  cursor: grab;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drag-handle:hover {
  color: #6b7280;
  background-color: #f3f4f6;
}

.preview-category {
  font-size: 8px;
  font-weight: 600;
  color: #3e3efe;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  display: block;
  margin-bottom: 4px;
}

.preview-title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.btn-close-preview {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-close-preview:hover {
  color: #374151;
}

.preview-body {
  flex: 1;
  overflow-y: auto;
  padding: 14px 16px;
}

.preview-section {
  margin-bottom: 16px;
}

.preview-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 9px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.preview-subject-text {
  font-size: 11px;
  color: #111827;
  font-weight: 500;
  margin: 0;
  line-height: 1.4;
}

.preview-shortcut-content {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  margin-top: 4px;
}

.shortcut-text {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  flex: 1;
}

.preview-content-text {
  font-size: 12px;
  color: #374151;
  line-height: 1.5;
}

.preview-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-top: 1px solid #e8e8e8;
  gap: 12px;
}

.preview-stats {
  display: flex;
  gap: 12px;
}

.preview-stat {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 600;
  background: transparent;
  border: none;
  padding: 0;
  cursor: default;
  outline: none;
  box-shadow: none;
}

.preview-stat:focus {
  outline: none;
  box-shadow: none;
}

.preview-stat.clickable {
  cursor: pointer;
  transition: all 0.2s ease;
}

.preview-stat.clickable:hover {
  transform: scale(1.1);
}

.preview-stat span {
  font-size: 10px;
}

.preview-stat.positive {
  color: #10b981;
}

.preview-stat.negative {
  color: #ef4444;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.btn-copy-template {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #3e3efe;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 10px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
}

.btn-copy-template:hover {
  background: #3230d6;
  transform: translateY(-1px);
}

.btn-copy-template .iconify {
  color: #ffffff;
}

.btn-copy-template .copy-count {
  font-size: 10px;
  font-weight: 500;
  color: #ffffff;
  background: transparent;
  padding: 0;
  border-radius: 0;
  min-width: auto;
  text-align: center;
}

.preview-actions {
  display: flex;
  gap: 6px;
  align-items: center;
}

.btn-edit,
.btn-delete {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #6b7280;
  outline: none;
  box-shadow: none;
}

.btn-edit:focus,
.btn-delete:focus {
  outline: none;
  box-shadow: none;
}

.btn-edit:hover {
  background: #f3f1ff;
  border-color: #3e3efe;
  color: #3e3efe;
}

.btn-delete:hover {
  background: #fef2f2;
  border-color: #ef4444;
  color: #ef4444;
}

@media (max-width: 640px) {
  .preview-modal-overlay {
    padding-right: 0;
    padding-left: 0;
    padding-top: 0;
    padding-bottom: 0;
    align-items: stretch;
    justify-content: stretch;
  }

  .preview-modal {
    min-width: 100vw !important;
    max-width: 100vw !important;
    width: 100vw !important;
    min-height: 100dvh !important;
    max-height: 100dvh !important;
    border-radius: 0 !important;
    right: 0 !important;
    left: 0 !important;
    top: 0 !important;
    transform: none !important;
  }
}
</style>
